import { Link } from '@inertiajs/react'
import { AnimatePresence, motion } from 'motion/react'
import { ShoppingBag, Trash2, X } from 'lucide-react'
import { useCart, lineKey } from '../lib/cart'
import { useT, useLocale, useShared } from '../lib/i18n'
import { formatDA } from '../lib/format'
import { Button } from './ui/Button'
import { QuantityStepper } from './ui/QuantityStepper'
import { UpsellRow } from './UpsellRow'

export function CartDrawer() {
  const { isOpen, closeCart, lines, setQty, removeItem, subtotal, count } = useCart()
  const t = useT()
  const { isRtl } = useLocale()
  const { upsell_products } = useShared()

  // Slide from the inline-end edge: right in LTR, left in RTL.
  const offscreen = isRtl ? '-100%' : '100%'

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: offscreen }}
            animate={{ x: 0 }}
            exit={{ x: offscreen }}
            transition={{ type: 'spring', stiffness: 360, damping: 38 }}
            className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl"
          >
            <header className="flex items-center justify-between border-b border-ink-100 p-5">
              <div className="flex items-center gap-2">
                <ShoppingBag className="size-5" />
                <h2 className="text-lg font-bold">{t('cart.title')}</h2>
                {count > 0 && (
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-semibold">
                    {count}
                  </span>
                )}
              </div>
              <button
                onClick={closeCart}
                aria-label="close"
                className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-5" />
              </button>
            </header>

            {lines.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 text-ink-400">
                <ShoppingBag className="size-12" strokeWidth={1.2} />
                <p>{t('cart.empty')}</p>
                <Button variant="outline" onClick={closeCart}>
                  {t('common.continue_shopping')}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex-1 divide-y divide-ink-100 overflow-y-auto px-5">
                  <AnimatePresence initial={false}>
                    {lines.map((line) => (
                      <motion.div
                        key={lineKey(line)}
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex gap-3 py-4"
                      >
                        <div className="size-20 shrink-0 overflow-hidden rounded-xl bg-ink-50">
                          {line.image_url && (
                            <img
                              src={line.image_url}
                              alt={line.name}
                              className="size-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <Link
                              href={`/p/${line.slug}`}
                              onClick={closeCart}
                              className="text-sm font-semibold text-ink-900 hover:text-accent-600"
                            >
                              {line.name}
                            </Link>
                            <button
                              onClick={() => removeItem(lineKey(line))}
                              aria-label={t('cart.remove')}
                              className="text-ink-400 hover:text-red-600"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                          {(line.color || line.size) && (
                            <p className="mt-0.5 text-xs text-ink-500">
                              {[line.color, line.size].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          <p className="mt-0.5 text-sm text-ink-500">{formatDA(line.price)}</p>
                          <div className="mt-auto flex items-center justify-between pt-2">
                            <QuantityStepper
                              value={line.qty}
                              onChange={(q) => setQty(lineKey(line), q)}
                            />
                            <span className="font-bold">{formatDA(line.price * line.qty)}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {upsell_products && upsell_products.length > 0 && (
                    <div className="py-5">
                      <UpsellRow products={upsell_products} title={t('upsell.cart')} compact />
                    </div>
                  )}
                </div>

                <footer className="space-y-4 border-t border-ink-100 p-5">
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>{t('common.subtotal')}</span>
                    <span>{formatDA(subtotal)}</span>
                  </div>
                  <Button href="/checkout" size="lg" fullWidth onClick={closeCart}>
                    {t('cart.checkout')}
                  </Button>
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
